/**
 * Resizable Layout - Pure vanilla JS implementation
 * Adds draggable splitters between left sidebar, main content, and right sidebar
 */

const STORAGE_KEY = 'project-layout-widths';
const LEFT_DEFAULT = 320;
const RIGHT_DEFAULT = 386;
const LEFT_MIN = LEFT_DEFAULT - 80;
const LEFT_MAX = LEFT_DEFAULT + 80;
const RIGHT_MIN = RIGHT_DEFAULT - 80;
const RIGHT_MAX = RIGHT_DEFAULT + 80;

const MEETINGS_STORAGE_KEY = 'meetings-layout-widths';
const MEETINGS_LEFT_DEFAULT = 280;
const MEETINGS_LEFT_MIN = 220;
const MEETINGS_LEFT_MAX = 400;

const MOBILE_BREAKPOINT = 1024;

let leftWidth = LEFT_DEFAULT;
let rightWidth = RIGHT_DEFAULT;
let meetingsLeftWidth = MEETINGS_LEFT_DEFAULT;
let isResizingLeft = false;
let isResizingRight = false;
let isResizingMeetingsLeft = false;
let startX = 0;
let startWidth = 0;

// Track last known mobile state for transition detection
let _wasMobile = window.innerWidth < MOBILE_BREAKPOINT;

// Export for SPA
window.initializeResizableLayout = initializeResizableLayout;

document.addEventListener('DOMContentLoaded', () => {
  initializeResizableLayout();
  setupResizeListener();
});

document.addEventListener('turbo:load', () => {
  initializeResizableLayout();
  setupResizeListener();
});

// --- Mobile/Desktop transition handling ---

function isMobileViewport() {
  return window.innerWidth < MOBILE_BREAKPOINT;
}

function managedSidebarWidth(sidebar) {
  if (!sidebar || sidebar.dataset.resizableWidth === undefined) return null;

  const width = Number(sidebar.dataset.resizableWidth);
  return Number.isFinite(width) ? width : null;
}

function sidebarCanResize(sidebar) {
  if (!sidebar) return false;

  const managedWidth = managedSidebarWidth(sidebar);
  return managedWidth === null ? sidebar.style.width !== '0px' : managedWidth > 0;
}

function restoreManagedSidebarWidth(sidebar) {
  if (!sidebar) return;

  const managedWidth = managedSidebarWidth(sidebar);
  sidebar.style.width = managedWidth === null ? '' : `${managedWidth}px`;
}

function teardownResizeHandlers() {
  const pageContainer = document.querySelector('[data-controller="panel-switcher"]');
  if (!pageContainer) return;

  // Remove splitters
  const splitters = pageContainer.querySelectorAll('.resizable-splitter');
  splitters.forEach(s => s.remove());

  // Remove initialized flag so desktop can re-init
  pageContainer.removeAttribute('data-resizable-initialized');

  // Remove mouse handlers
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);

  // Restore React-owned widths after removing imperative resize handlers.
  // Clearing these values made a collapsed 0px sidebar reserve its old
  // desktop width after a mobile → desktop breakpoint round-trip.
  const leftSidebar = pageContainer.querySelector('[data-panel-switcher-target="projectSidebar"]');
  const meetingsSidebar = pageContainer.querySelector('[data-panel-switcher-target="meetingsSidebar"]');
  const rightSidebar = pageContainer.querySelector('[data-panel-switcher-target="rightSidebar"]');
  restoreManagedSidebarWidth(leftSidebar);
  if (meetingsSidebar) meetingsSidebar.style.width = '';
  restoreManagedSidebarWidth(rightSidebar);
}

let _resizeDebounceTimer = null;

function setupResizeListener() {
  // Avoid attaching multiple listeners
  if (window._resizableResizeListenerAttached) return;
  window._resizableResizeListenerAttached = true;

  window.addEventListener('resize', () => {
    clearTimeout(_resizeDebounceTimer);
    _resizeDebounceTimer = setTimeout(() => {
      const nowMobile = isMobileViewport();

      if (_wasMobile && !nowMobile) {
        // mobile → desktop: initialize resize handlers
        _wasMobile = false;
        initializeResizableLayout();
      } else if (!_wasMobile && nowMobile) {
        // desktop → mobile: tear down resize handlers
        _wasMobile = true;
        teardownResizeHandlers();
      }
    }, 150);
  });
}

function initializeResizableLayout() {
  // Skip on mobile — panels are full-width, no resize needed
  if (isMobileViewport()) {
    return;
  }

  const pageContainer = document.querySelector('[data-controller="panel-switcher"]');
  if (!pageContainer) {
    return;
  }
  
  // Skip in share mode (when URL has ?share= parameter)
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('share')) {
    console.log('[ResizableLayout] Share mode detected, skipping initialization and cleaning up');
    // Remove any existing splitters
    const oldSplitters = pageContainer.querySelectorAll('.resizable-splitter');
    oldSplitters.forEach(splitter => splitter.remove());
    return;
  }
  
  if (pageContainer.hasAttribute('data-resizable-initialized')) {
    return;
  }

  const leftSidebar = pageContainer.querySelector('[data-panel-switcher-target="projectSidebar"]');
  const meetingsSidebar = pageContainer.querySelector('[data-panel-switcher-target="meetingsSidebar"]');
  const workspaceContainer = pageContainer.querySelector('[data-panel-switcher-target="workspaceContainer"]');
  const rightSidebar = pageContainer.querySelector('[data-panel-switcher-target="rightSidebar"]');

  // Need at least workspace container
  if (!workspaceContainer) {
    console.warn('[ResizableLayout] Workspace container not found');
    return;
  }
  
  // If neither sidebar exists, nothing to resize
  if (!leftSidebar && !rightSidebar && !meetingsSidebar) {
    console.warn('[ResizableLayout] No sidebars found');
    return;
  }

  // Load saved widths
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      leftWidth = parsed.left || LEFT_DEFAULT;
      rightWidth = parsed.right || RIGHT_DEFAULT;
    } catch (e) {
      console.error('[ResizableLayout] Failed to parse saved widths:', e);
    }
  }

  // Load saved meetings widths
  const savedMeetings = localStorage.getItem(MEETINGS_STORAGE_KEY);
  if (savedMeetings) {
    try {
      meetingsLeftWidth = JSON.parse(savedMeetings).left || MEETINGS_LEFT_DEFAULT;
    } catch (e) {}
  }

  // Apply initial widths — but respect collapsed state set by React.
  if (sidebarCanResize(leftSidebar)) {
    leftSidebar.style.width = `${leftWidth}px`;
  }
  if (meetingsSidebar) {
    meetingsSidebar.style.width = `${meetingsLeftWidth}px`;
  }
  if (sidebarCanResize(rightSidebar)) {
    rightSidebar.style.width = `${rightWidth}px`;
  }

  // Create left splitter (only if leftSidebar exists)
  if (sidebarCanResize(leftSidebar)) {
    const leftSplitter = createSplitter('left');
    leftSidebar.insertAdjacentElement('afterend', leftSplitter);
    leftSplitter.addEventListener('mousedown', (e) => handleMouseDown(e, 'left'));
  }

  // Create meetings left splitter (only if meetingsSidebar exists)
  if (meetingsSidebar) {
    const meetingsSplitter = createSplitter('meetingsLeft');
    meetingsSidebar.insertAdjacentElement('afterend', meetingsSplitter);
    meetingsSplitter.addEventListener('mousedown', (e) => handleMouseDown(e, 'meetingsLeft'));
  }

  // Create right splitter (only if rightSidebar exists)
  if (sidebarCanResize(rightSidebar)) {
    const rightSplitter = createSplitter('right');
    workspaceContainer.insertAdjacentElement('afterend', rightSplitter);
    rightSplitter.addEventListener('mousedown', (e) => handleMouseDown(e, 'right'));
  }

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);

  pageContainer.setAttribute('data-resizable-initialized', 'true');

  // Store references for cleanup
  window._resizableCleanup = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };
}

function createSplitter(side) {
  const splitter = document.createElement('div');
  splitter.className = `resizable-splitter resizable-splitter-${side}`;
  splitter.style.cssText = `
    width: 0px;
    flex-shrink: 0;
    cursor: ew-resize;
    position: relative;
    z-index: 1;
  `;

  // Expanded invisible hit area for grabbing
  const hitArea = document.createElement('div');
  hitArea.style.cssText = `
    position: absolute;
    top: 0;
    bottom: 0;
    left: -4px;
    width: 8px;
    cursor: ew-resize;
  `;
  splitter.appendChild(hitArea);

  // Thin 1px line visible on hover with gradient fade
  const line = document.createElement('div');
  line.className = 'splitter-line';
  line.style.cssText = `
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 1px;
    background: linear-gradient(to bottom, 
      rgba(55, 65, 81, 0) 0%, 
      rgba(55, 65, 81, 1) 10%, 
      rgba(55, 65, 81, 1) 90%, 
      rgba(55, 65, 81, 0) 100%);
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
  `;
  splitter.appendChild(line);

  // Visual handle (lime slider) - follows cursor vertically
  const handle = document.createElement('div');
  handle.className = 'splitter-handle';
  handle.style.cssText = `
    position: absolute;
    left: 0;
    transform: translateX(-50%);
    width: 4px;
    height: 48px;
    background-color: #d7ff00;
    border-radius: 2px;
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
    top: 50%;
    margin-top: -24px;
  `;
  splitter.appendChild(handle);

  // Track mouse movement to position handle under cursor
  hitArea.addEventListener('mousemove', (e) => {
    const rect = splitter.getBoundingClientRect();
    const y = e.clientY - rect.top;
    handle.style.top = `${y}px`;
    handle.style.marginTop = '-24px';
  });

  // Hover effects on hitArea
  hitArea.addEventListener('mouseenter', () => {
    line.style.opacity = '1';
    handle.style.opacity = '1';
  });

  hitArea.addEventListener('mouseleave', () => {
    if (!isResizingLeft && !isResizingRight) {
      line.style.opacity = '0';
      handle.style.opacity = '0';
    }
  });

  return splitter;
}

function handleMouseDown(e, side) {
  e.preventDefault();
  startX = e.clientX;
  
  if (side === 'left') {
    isResizingLeft = true;
    startWidth = leftWidth;
  } else if (side === 'meetingsLeft') {
    isResizingMeetingsLeft = true;
    startWidth = meetingsLeftWidth;
  } else {
    isResizingRight = true;
    startWidth = rightWidth;
  }
  
  document.body.style.cursor = 'ew-resize';
  document.body.style.userSelect = 'none';
}

function handleMouseMove(e) {
  if (!isResizingLeft && !isResizingRight && !isResizingMeetingsLeft) return;

  const pageContainer = document.querySelector('[data-controller="panel-switcher"]');
  if (!pageContainer) return;

  if (isResizingLeft) {
    const delta = e.clientX - startX;
    const newWidth = Math.max(LEFT_MIN, Math.min(LEFT_MAX, startWidth + delta));
    leftWidth = newWidth;

    const leftSidebar = pageContainer.querySelector('[data-panel-switcher-target="projectSidebar"]');
    if (sidebarCanResize(leftSidebar)) {
      leftSidebar.style.width = `${leftWidth}px`;
    }
  }

  if (isResizingMeetingsLeft) {
    const delta = e.clientX - startX;
    const newWidth = Math.max(MEETINGS_LEFT_MIN, Math.min(MEETINGS_LEFT_MAX, startWidth + delta));
    meetingsLeftWidth = newWidth;

    const meetingsSidebar = pageContainer.querySelector('[data-panel-switcher-target="meetingsSidebar"]');
    if (meetingsSidebar) {
      meetingsSidebar.style.width = `${meetingsLeftWidth}px`;
    }
  }

  if (isResizingRight) {
    const delta = startX - e.clientX;
    const newWidth = Math.max(RIGHT_MIN, Math.min(RIGHT_MAX, startWidth + delta));
    rightWidth = newWidth;

    const rightSidebar = pageContainer.querySelector('[data-panel-switcher-target="rightSidebar"]');
    if (sidebarCanResize(rightSidebar)) {
      rightSidebar.style.width = `${rightWidth}px`;
    }
    
    // Dispatch event for CommentsPanelNew to listen
    window.dispatchEvent(new CustomEvent('right-panel-resized', {
      detail: { width: rightWidth }
    }));
  }
}

function handleMouseUp() {
  if (isResizingLeft || isResizingRight || isResizingMeetingsLeft) {
    isResizingLeft = false;
    isResizingRight = false;
    isResizingMeetingsLeft = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      left: leftWidth,
      right: rightWidth
    }));
    localStorage.setItem(MEETINGS_STORAGE_KEY, JSON.stringify({
      left: meetingsLeftWidth
    }));

    // Reset line and handle opacity
    document.querySelectorAll('.splitter-line').forEach(line => {
      line.style.opacity = '0';
    });
    document.querySelectorAll('.splitter-handle').forEach(handle => {
      handle.style.opacity = '0';
    });
  }
}

export default initializeResizableLayout;
